<template>
  <div>
    <div class="details-component">
      <div class="details-component__header">
        <h1>{{ $t('parking.parking') }}</h1>
        <div class="details-component__header_right" v-if="hasNotEnded() || hasNotStarted()">
          <router-link :to="{ name: 'edit-parking', params: { id: parking.id } }" class="link">
            {{ $t('parking.editParking') }}&nbsp;<i class="icon icon--arrow"></i>
          </router-link>
        </div>
      </div>
      <div class="details-component__content content">
        <div class="content__cell">
          <table class="details-table no-col-padding label-r-padding fixed-table label-col-wide-150">
            <tr class="details-table__row" v-if="isSuperAdmin()">
              <td class="details-table__col details-table__col_label">{{ $t('common.landlord') }}</td>
              <td class="details-table__col landlord__name" v-if="parking.partner">
                <router-link
                  :to="{ name: 'landlord', params: { id: parking.partner.landlord.id }, query: { keyword: parking.partner.landlord.name } }"
                  class="link link--blue">
                  {{ parking.partner.landlord.name }}
                </router-link>
              </td>
            </tr>
            <tr class="details-table__row" v-if="isLandlord() && parking.partner">
              <td class="details-table__col details-table__col_label">{{ $t('common.tenant') }}</td>
              <td class="details-table__col">
                <router-link
                  :to="{ name: 'tenant', params: { id: parking.partner.id }, query: { keyword: parking.partner.name } }"
                  class="link link--blue">
                  {{ parking.partner.name }}
                </router-link>
              </td>
            </tr>
            <tr class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('parking.vehicleReg') }}</td>
              <td class="details-table__col bold">{{ parking.vehicleReg }}</td>
            </tr>
            <tr v-if="parking.spot" class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('common.spot') }}</td>
              <td class="details-table__col">{{ parking.spot.split("_").join(" ") }}</td>
            </tr>
            <tr class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('parking.comment') }}</td>
              <td class="details-table__col">{{ parking.comment }}</td>
            </tr>
            <tr class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('parking.mobile') }}</td>
              <td class="details-table__col">{{ parking.mobile }}</td>
            </tr>
            <tr class="details-table__row" v-if="parking.product">
              <td class="details-table__col details-table__col_label">{{ $t('common.product') }}</td>
              <td class="details-table__col">
                <router-link
                  :to="{ name: 'product', params: { id: parking.product.id }, query: { keyword: parking.product.name } }"
                  class="link link--blue">
                  {{ parking.product.name }}
                </router-link>
              </td>
            </tr>
            <tr class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('parking.startTime') }}</td>
              <td class="details-table__col">{{ parking.startTime | moment }}</td>
            </tr>
            <tr class="details-table__row" v-if="parking.endTime">
              <td class="details-table__col details-table__col_label">{{ $t('parking.endTime') }}</td>
              <td class="details-table__col">{{ parking.endTime | moment }}</td>
            </tr>
            <tr class="details-table__row" v-if="parking.endTime">
              <td class="details-table__col details-table__col_label">{{ $t('parking.total') }}</td>
              <td class="details-table__col">{{ getTotalParkingTime() }}</td>
            </tr>
            <tr class="details-table__row" v-if="parking.price">
              <td class="details-table__col details-table__col_label">{{ $t('parking.price') }}</td>
              <td class="details-table__col">{{ parking.price.toFixed(2) }} {{ currency_short }}</td>
            </tr>
            <tr class="details-table__row" v-if="parking.created_by">
              <td class="details-table__col details-table__col_label">{{ $t('parking.created_by') }}</td>
              <td class="details-table__col">{{ formatCreatedBy(parking.created_by) }}</td>
            </tr>
          </table>
          <button class="btn btn--main" v-if="!isUnconfirmed() && hasNotEnded()" @click.prevent.stop="stopParking()">{{
          $t('parking.stop') }}</button>
          <button class="btn btn--main" v-if="hasNotStarted()" @click.prevent.stop="showDeletingConfirmation()">{{
          $t('parking.cancel') }}</button>
          <button class="btn btn--main" v-if="isUnconfirmed()" @click.prevent.stop="confirmParking()">{{
          $t('parking.confirm_session') }}</button>
          <button class="btn btn--secondary" v-if="parking.price && user.data.selectedRole.role === 'landlord'"
            @click.prevent.stop="showPriceDeleteConfirmation()">{{ $t('parking.price_delete') }}</button>
        </div>
        <div v-if="occupancy" class="calendar-container">
          <header class="calendar-header">
            <p class="calendar-title">
              Occupancy calendar
            </p>
            <div class="calendar-nav-container">
              <p class="calendar-current-date">
                {{ months[month] }} {{ year }}
              </p>
              <div class="calendar-navigation">
                <span id="calendar-prev" class="material-symbols-rounded" @click="updateCalendarDate('decrement')">
                  &larr;
                </span>
                <span id="calendar-next" class="material-symbols-rounded" @click="updateCalendarDate()">&rarr;</span>
              </div>
            </div>
          </header>
          <div class="calendar-body">
            <ul class="calendar-weekdays">
              <li>Sun</li>
              <li>Mon</li>
              <li>Tue</li>
              <li>Wed</li>
              <li>Thu</li>
              <li>Fri</li>
              <li>Sat</li>
            </ul>
            <ul class="calendar-dates" v-html="createDaysComponents()" />
          </div>
        </div>
      </div>
    </div>
    <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelParkingDelete" @confirmed="deleteParking">
      <h1 slot="header">{{ $t('common.deleting') }}: {{ parking.vehicleReg }} </h1>
      <p slot="body">{{ $t('parking.confirm_delete') }}</p>
    </ConfirmationModal>
    <ConfirmationModal v-if="showPriceDeleteConfirmationModal" @cancel="cancelPriceDelete"
      @confirmed="parkingPriceDelete">
      <h1 slot="header">{{ $t('parking.price_delete_info') }}: {{ parking.vehicleReg }} </h1>
      <p slot="body">{{ $t('parking.confirm_price_delete') }}</p>
    </ConfirmationModal>
    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ warningHeader }}</h1>
      <p slot="body">{{ warningBody }}</p>
    </WarningModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import * as mTypes from 'store/mutation-types';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';

export default {
  components: {
    ConfirmationModal,
    WarningModal,
  },
  computed: {
    ...mapGetters({
      parking: 'parkings/parking/parking',
      parkingError: 'parkings/parking/error',
      parkingsLimit: 'landlord/parkings/limit',
      parkingsKeywordFilter: 'landlord/parkings/keywordFilter',
      coordinates: 'parkings/parking/coordinates',
      occupancy: 'parkings/parking/occupancy',
    }),
    month() {
      return this.date.getMonth();
    },
    year() {
      return this.date.getFullYear();
    },
    defaultTime() {
      return moment().set('second', 0).format('HH:mm:ss');
    },
    computedCalendarData() {
      const dayone = new Date(this.year, this.month, 1).getDay();
      const lastdate = new Date(this.year, this.month + 1, 0).getDate();
      const dayend = new Date(this.year, this.month, lastdate).getDay();
      const monthlastdate = new Date(this.year, this.month, 0).getDate();

      return { dayone, dayend, lastdate, monthlastdate };
    },
    occupancyDuringSelectedMonth() {
      const days = Object.entries(this.occupancy).filter(day => day[0].includes(`${this.year}-0${this.month + 1}`));
      return Object.fromEntries(days);
    },
  },
  watch: {
    $route() {
      if (String(this.$route.params.id) !== String(this.parking.id)) {
        this.fetchParking();
      }
    },
    parking: 'fetchOccupancy',
  },
  route: {
    canReuse: true,
  },
  created() {
    if (this.$route.params.id) {
      this.fetchParking();
    }
  },
  methods: {
    updateCalendarDate(method) {
      const updatedMonth = method === 'decrement' ? this.month - 1 : this.month + 1;

      if (updatedMonth < 0 || updatedMonth > 11) {
        this.date = new Date(this.year, updatedMonth, new Date().getDate());
      } else {
        this.date = new Date(this.year, updatedMonth, new Date().getDate());
      }

      this.fetchOccupancy(updatedMonth);
    },
    formatMonths(month) {
      if (Number(month) < 10) {
        return `0${month}`;
      }
      return String(month);
    },
    formatDays(day) {
      if (Number(day) < 10) {
        return `0${day}`;
      }
      return String(day);
    },
    createDaysComponents() {
      const { dayone, dayend, lastdate, monthlastdate } = this.computedCalendarData
      let lit = '';

      for (let i = dayone; i > 0; i--) {
        lit += `<li class="inactive">${monthlastdate - i + 1}</li>`;
      }

      for (let i = 1; i <= lastdate; i++) {
        const formattedMonth = this.formatMonths(this.month + 1);
        const formattedDay = this.formatDays(i);
        const isToday = i === this.date.getDate() && this.month === new Date().getMonth() && this.year === new Date().getFullYear() ? 'active' : '';
        const currentDayOccupancy = this.occupancyDuringSelectedMonth[`${this.year}-${formattedMonth}-${formattedDay}`];

        lit += `<li class="${isToday}"><div class="calendar-day"><span>${i}</span><span class="occupancy-value">${currentDayOccupancy || 0}</span></div></li>`;
      }

      for (let i = dayend; i < 6; i++) {
        lit += `<li class="inactive">${i - dayend + 1}</li>`;
      }

      return lit;
    },
    fetchOccupancy(month) {
      const selectedMonth = month || this.month;
      if (this.user.data) {
        this.$store.dispatch('parkings/parking/fetchOccupancy', { month: selectedMonth, productID: this.parking ? this.parking.product.id : undefined });
      }
    },
    fetchParking() {
      if (this.$route.params.id && this.user.data) {
        this.$store.dispatch('parkings/parking/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
          if (this.parking.total === 0) {
            this.$router.push({ name: 'add-parking' });
          }
        });
      }
    },
    getTotalParkingTime() {
      const start = moment(this.parking.startTime);
      const end = moment(this.parking.endTime);
      if (end.isBefore(moment(start.toDate()))) {
        return '-';
      }
      return this.formatPeriod(Math.floor(end.diff(start) / 60000));
    },
    formatPeriod(minutes, unit) {
      if (unit === 'month') {
        return `${minutes} ${this.$tc('pricingrules.month', minutes)}`;
      }
      let min = minutes;
      let days = Math.floor(min / this.minutes_in_day);
      min -= days * this.minutes_in_day;
      let hours = Math.floor(min / this.minutes_in_hour);
      min -= hours * this.minutes_in_hour;
      if (days === 1) {
        days = `${days} ${this.$tc('pricingrules.day', 1)} `;
      } else if (days > 1) {
        days = `${days} ${this.$tc('pricingrules.day', 2)} `;
      } else {
        days = '';
      }
      if (hours) {
        hours = `${hours} ${this.$t('pricingrules.hour')} `;
      } else {
        hours = '';
      }
      if (min) {
        min = `${min} ${this.$t('pricingrules.minute')} `;
      } else {
        min = '';
      }
      return (days + hours + min).trim();
    },
    isUnconfirmed() {
      return this.parking.status === 'waiting_confirmation';
    },
    hasNotEnded() {
      return moment(this.parking.startTime).isBefore(moment()) &&
        (!this.parking.endTime || moment().isBefore(moment(this.parking.endTime)));
    },
    hasNotStarted() {
      return moment().isBefore(moment(this.parking.startTime));
    },
    showEditGuest() {
      return moment().diff(moment(this.parking.startTime), 'seconds') < 15 * 60;
    },
    confirmParking() {
      this.$store.dispatch('parkings/parking/confirm', { id: this.parking.id, user: this.user })
        .then(() => {
          this.showConfirmationModal = false;
          if (this.parkingError) {
            this.warningHeader = this.$t('common.error');
            this.warningBody = this.$t(`errors.${this.parkingError}`);
            this.showWarningModal = true;
          } else {
            this.$store.dispatch('landlord/parkings/fetch', { user: this.user, limit: this.parkingsLimit, keyword: this.parkingsKeywordFilter });
            this.$router.push({ name: 'parkings' });
          }
        })
        .catch(() => {
          this.$store.commit(`parkings/parking/${mTypes.DATA_ERROR}`, true);
        });
    },
    stopParking() {
      this.showConfirmationModal = false;
      const time = { endTime: moment().format('YYYY-MM-DD HH:mm:ss') };
      this.$store.dispatch('parkings/parking/stop', { id: this.parking.id, endTime: time, user: this.user })
        .then(() => {
          if (this.parkingError) {
            this.warningHeader = this.$t('common.deleting');
            this.warningBody = this.$t(`errors.${this.parkingError}`);
            this.showWarningModal = true;
          } else {
            this.$store.dispatch('landlord/parkings/fetch', { user: this.user, limit: this.parkingsLimit, keyword: this.parkingsKeywordFilter });
            this.$router.push({ name: 'parkings' });
          }
        })
        .catch(() => {
          this.$store.commit(`parkings/parking/${mTypes.DATA_ERROR}`, true);
        });
    },
    deleteParking() {
      this.showConfirmationModal = false;
      this.$store.dispatch('parkings/parking/delete', { id: this.parking.id, user: this.user })
        .then(() => {
          if (this.parkingError) {
            this.warningHeader = this.$t('common.deleting');
            this.warningBody = this.$t(`errors.${this.parkingError}`);
            this.showWarningModal = true;
          } else {
            this.$store.commit(`landlord/parkings/${mTypes.REMOVE_DATA}`, this.parking.id);
            this.$router.push({ name: 'parkings' });
          }
        })
        .catch(() => {
          this.$store.commit(`parkings/parking/${mTypes.DATA_ERROR}`, true);
        });
    },
    showDeletingConfirmation() {
      this.showConfirmationModal = true;
    },
    cancelParkingDelete() {
      this.showConfirmationModal = false;
    },
    closeWarningModal() {
      this.showWarningModal = false;
    },
    showPriceDeleteConfirmation() {
      this.showPriceDeleteConfirmationModal = true;
    },
    cancelPriceDelete() {
      this.showPriceDeleteConfirmationModal = false;
    },
    parkingPriceDelete() {
      this.showPriceDeleteConfirmationModal = false;
      this.$store.dispatch('parkings/parking/delete_price', { id: this.parking.id, user: this.user })
        .then(() => {
          if (this.parkingError) {
            this.warningHeader = this.$t('parking.price_clear');
            this.warningBody = this.$t(`errors.${this.parkingError}`);
            this.showWarningModal = true;
          } else {
            this.fetchParking();
          }
        })
        .catch(() => {
          this.$store.commit(`parkings/parking/${mTypes.DATA_ERROR}`, true);
        });
    },
    formatCreatedBy(createdBy) {
      let type = createdBy.type;
      type = type.substring(type.lastIndexOf('\\') + 1);
      if (type === 'Account') {
        return createdBy.name;
      } else if (type === 'PartnerKiosk') {
        return this.$t('parking.kiosk');
      }
      return type;
    },
  },
  data() {
    return {
      currency: process.env.CURRENCY,
      currency_short: process.env.CURRENCY_SHORT,
      minutes_in_hour: 60,
      minutes_in_day: 24 * 60,
      showConfirmationModal: false,
      showPriceDeleteConfirmationModal: false,
      showWarningModal: false,
      warningBody: null,
      warningHeader: null,
      zoom: 15,
      center: [24.707, 59.412],
      style: 'klokantech-basic',
      mapInstance: null,
      months: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'],
      date: new Date(),
    };
  },
  filters: {
    moment(date) {
      return moment(date).format('DD.MM.YYYY HH:mm');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/blocks/details.scss';

.btn {
  min-width: 100%;
}

#parking-map-dom {
  margin-top: 30px;
  width: 100%;
  height: 300px;
}
</style>



// WEBPACK FOOTER //
// src/components/parkings/ParkingDetails.vue